import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  startRegistration as wkStartRegistration,
  startAuthentication as wkStartAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { apiBase } from '@/utils/apiBase';

const AuthContext = createContext(null);

const API = apiBase();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('hc_token') || '');

  const axiosInstance = React.useMemo(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    instance.interceptors.request.use(config => {
      const t = localStorage.getItem('hc_token');
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const storedToken = localStorage.getItem('hc_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    try {
      const res = await axiosInstance.get('/auth/me');
      setUser(res.data);
    } catch {
      localStorage.removeItem('hc_token');
      setToken('');
    }
    setLoading(false);
  }, [axiosInstance]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axiosInstance.post('/auth/login', { email, password });
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      return { requires_2fa: true, email: res.data.email };
    }
    localStorage.setItem('hc_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const verify2FA = async (email, code) => {
    const res = await axiosInstance.post('/auth/2fa/validate', { email, code });
    localStorage.setItem('hc_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axiosInstance.post('/auth/register', { email, password, name });
    if (res.data.pending_approval) {
      return res.data;
    }
    localStorage.setItem('hc_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ---- Passkey (WebAuthn) flows ----
  const passkeySupported = browserSupportsWebAuthn();

  const registerPasskey = async (deviceName = 'My Device') => {
    // 1. Get options from server
    const beginRes = await axiosInstance.post('/auth/passkey/register/begin', { device_name: deviceName });
    // 2. Browser prompts for biometric
    const credential = await wkStartRegistration({ optionsJSON: beginRes.data });
    // 3. Send result back to verify + persist
    const completeRes = await axiosInstance.post('/auth/passkey/register/complete', {
      credential, device_name: deviceName,
    });
    return completeRes.data;
  };

  const loginWithPasskey = async () => {
    const beginRes = await axiosInstance.post('/auth/passkey/login/begin', {});
    const credential = await wkStartAuthentication({ optionsJSON: beginRes.data });
    const completeRes = await axiosInstance.post('/auth/passkey/login/complete', { credential });
    localStorage.setItem('hc_token', completeRes.data.token);
    setToken(completeRes.data.token);
    setUser(completeRes.data.user);
    return completeRes.data;
  };

  const submitRecoveryCode = async (email, code, newPassword) => {
    const res = await axios.post(`${API}/auth/recovery/use`, {
      email, code, new_password: newPassword,
    });
    return res.data;
  };

  const setSessionUser = (userData, sessionToken) => {
    localStorage.setItem('hc_token', sessionToken);
    setToken(sessionToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch { /* ignore */ }
    localStorage.removeItem('hc_token');
    setToken('');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, token, login, verify2FA, register, loginWithGoogle, setSessionUser, logout,
      registerPasskey, loginWithPasskey, submitRecoveryCode, passkeySupported, checkAuth,
      api: axiosInstance,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
